import { TimelineEventType } from './timeline';

/**
 * Report draft types — persistent, named, admin-shared drafts for the timeline
 * PDF report. Stored at `reportDrafts/{draftKey}` (admin-only DB rules).
 *
 * Conventions:
 * - User identification via UID only (no PII)
 * - Date format: YYYY-MM-DD hh:mm:ss (Europe/Stockholm timezone)
 *
 * A draft is a self-contained snapshot (plan D14): at creation, the filter
 * step seeds member events with their printed lines and photo references
 * copied in. Membership and content are fixed from then on — later timeline
 * edits never reach a draft, and nothing here is ever written back to
 * timeline sources. Curation (exclusions, text edits, comments, paragraphs)
 * lives entirely in the draft.
 */

/** Printed lines of an event that can be hidden or text-overridden per draft. */
export type ReportDraftFieldId = 'description' | 'buildingName' | 'completionComment' | 'measurement';

export const REPORT_DRAFT_FIELD_IDS: readonly ReportDraftFieldId[] =
    ['description', 'buildingName', 'completionComment', 'measurement'];

export type ReportDraftLocationLevel = 'category1' | 'category2' | 'category3' | 'category4' | 'category5';

/**
 * The draft's building scope — one choice, made at creation, used only to seed
 * the draft's membership (the draft is fixed once created):
 * - 'all': every building
 * - 'tag': buildings carrying a specific group tag
 * - 'location': buildings with a specific location category value at one level
 * - 'buildings': a handpicked list
 */
export interface ReportDraftScope {
    type: 'all' | 'tag' | 'location' | 'buildings';
    /** Set when type === 'tag'. */
    tagKey?: string;
    /** Set when type === 'location'. */
    locationLevel?: ReportDraftLocationLevel;
    locationKey?: string;
    /** Set when type === 'buildings'. */
    buildingKeys?: string[];
}

/**
 * Seed filters, fixed once the draft is created (no filter editing, no re-sync).
 * eventTypes absent = the explicit "Alla" choice (no filtering on type).
 */
export interface ReportDraftFilters {
    startYear: number;
    endYear: number;
    eventTypes?: TimelineEventType[];
    scope: ReportDraftScope;
}

/** One printed line of an event, snapshotted at creation and editable draft-locally. */
export interface ReportDraftItemField {
    /** The text printed in the report (edited freely in the draft). */
    text: string;
    /** Line omitted from the PDF when true. */
    hidden?: boolean;
    /** The text at snapshot time — the reset target. */
    original: string;
}

/** One photo of an event, snapshotted at creation (reference by storage path). */
export interface ReportDraftItemPhoto {
    storagePath: string;
    caption?: string;
    /** Photo omitted from the PDF when true. */
    excluded?: boolean;
    /** Display/print order within the event. */
    sortOrder: number;
}

/**
 * One member event, fully snapshotted at draft creation (plan D14): the draft
 * is a self-contained document — later timeline edits never reach it. Keyed by
 * the stable event ID from `utils/timelineEventIds` (unique at seed time).
 */
export interface ReportDraftItem {
    /** false = user-excluded; the editor shows it greyed for re-inclusion. */
    included: boolean;
    comment?: string;
    eventType: TimelineEventType;
    /** Source building key at snapshot time (summary counting/provenance). */
    buildingKey?: string;
    /** Event date as snapshotted: YYYY-MM-DD hh:mm:ss or YYYY-MM-DD. */
    date: string;
    /** If true, only the year is significant (printed as the year). */
    inexactDate?: boolean;
    /** Printed lines present for this event type, keyed by field id. */
    fields?: { [field in ReportDraftFieldId]?: ReportDraftItemField };
    /** Keyed by the photo's/attachment's Firebase map key at snapshot time. */
    photos?: { [photoKey: string]: ReportDraftItemPhoto };
}

export type ReportDraftBlockAnchorType = 'document' | 'year' | 'event';

/** Free-text paragraph anchored into the report. */
export interface ReportDraftTextBlock {
    key: string;
    text: string;
    anchorType: ReportDraftBlockAnchorType;
    /** Set when anchorType === 'year'. */
    anchorYear?: number;
    /** Set when anchorType === 'event'. */
    anchorEventId?: string;
    /** Ordering among blocks sharing an anchor. */
    sortOrder: number;
}

export interface ReportDraft {
    key: string;
    /** Internal working name shown in the drafts list. */
    name: string;
    /** Title printed in the PDF. */
    reportTitle: string;
    archived?: boolean;
    filters: ReportDraftFilters;
    items?: { [eventId: string]: ReportDraftItem };
    textBlocks?: { [blockKey: string]: ReportDraftTextBlock };
    createdBy: string;        // UID
    createdAt: string;        // YYYY-MM-DD hh:mm:ss
    updatedBy?: string;       // UID
    updatedAt?: string;       // YYYY-MM-DD hh:mm:ss
}
